// VicTech #710:favicon 叠红点(canvas 动态生成 PNG data URL)。
// - 以当下每个 <link rel~="icon"> 的 href 为底图;别的程式(如 Chatwoot 自带的 faviconHelper)改了 href,
//   我们把新 href 当新底图,红点状态下再叠一次,不会互相覆盖掉。
// - 红点消失 = 还原成最近一次的底图 href。
const SIZE = 64;
export const DOT_COLOR = '#E5484D';

export const defaultLoadImage = src =>
  new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

export const defaultDrawBadge = image => {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext && canvas.getContext('2d');
  if (!ctx) return null;
  if (image) ctx.drawImage(image, 0, 0, SIZE, SIZE);
  const r = SIZE * 0.24;
  const cx = SIZE - r - 2;
  const cy = r + 2;
  ctx.beginPath();
  ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = DOT_COLOR;
  ctx.fill();
  try {
    return canvas.toDataURL('image/png');
  } catch (e) {
    return null; // 画布被跨域图污染 → 放弃叠加,不影响标题/声音
  }
};

export class FaviconBadge {
  constructor(
    doc = document,
    { loadImage = defaultLoadImage, drawBadge = defaultDrawBadge } = {}
  ) {
    this.doc = doc;
    this.loadImage = loadImage;
    this.drawBadge = drawBadge;
    this.active = false;
    this.base = new Map(); // link -> 底图 href
    this.ours = new Map(); // link -> 我们设的 data URL
    this.cache = new Map(); // 底图 href -> Promise<data URL|null>
    this.observer = null;
    this.renderSeq = 0;
  }

  links() {
    return [...this.doc.querySelectorAll('link[rel~="icon"]')];
  }

  track(link) {
    if (!this.base.has(link)) this.base.set(link, link.getAttribute('href'));
  }

  start() {
    this.links().forEach(link => this.track(link));
    if (this.observer || typeof MutationObserver === 'undefined') return;
    this.observer = new MutationObserver(mutations => {
      let dirty = false;
      mutations.forEach(m => {
        if (
          m.type === 'attributes' &&
          m.target.matches?.('link[rel~="icon"]')
        ) {
          const href = m.target.getAttribute('href');
          if (href !== this.ours.get(m.target)) {
            this.base.set(m.target, href);
            this.ours.delete(m.target);
            dirty = true;
          }
        }
        if (m.type === 'childList') dirty = true;
      });
      if (dirty && this.active) this.render();
    });
    this.observer.observe(this.doc.head, {
      attributes: true,
      attributeFilter: ['href'],
      subtree: true,
      childList: true,
    });
  }

  badgeFor(href) {
    if (!this.cache.has(href)) {
      this.cache.set(
        href,
        this.loadImage(href).then(img => this.drawBadge(img))
      );
    }
    return this.cache.get(href);
  }

  async render() {
    this.renderSeq += 1;
    const seq = this.renderSeq;
    const links = this.links();
    links.forEach(link => this.track(link));
    await Promise.all(
      links.map(async link => {
        const data = await this.badgeFor(this.base.get(link));
        if (!data || seq !== this.renderSeq || !this.active) return;
        this.ours.set(link, data);
        link.setAttribute('href', data);
      })
    );
  }

  restore() {
    this.renderSeq += 1;
    this.links().forEach(link => {
      if (this.ours.has(link)) {
        this.ours.delete(link);
        const base = this.base.get(link);
        if (base) link.setAttribute('href', base);
      }
    });
  }

  set(active) {
    const next = !!active;
    if (next === this.active) return Promise.resolve();
    this.active = next;
    if (next) return this.render();
    this.restore();
    return Promise.resolve();
  }

  stop() {
    this.active = false;
    this.restore();
    if (this.observer) this.observer.disconnect();
    this.observer = null;
  }
}
